import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Settings, Database, FileCheck, Building2, MapPin, ReceiptText, Users, Paperclip } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';

type SystemStats = {
  orgs: number;
  sites: number;
  bills: number;
  contacts: number;
  attachments: number;
};

async function loadStats(): Promise<SystemStats> {
  const [orgs, sites, bills, contacts, attachments] = await Promise.all([
    supabase.from('client').select('id', { count: 'exact', head: true }),
    supabase.from('site').select('id', { count: 'exact', head: true }),
    supabase.from('bv_bill').select('id', { count: 'exact', head: true }),
    supabase.from('contact').select('id', { count: 'exact', head: true }),
    supabase.from('bv_attachment').select('id', { count: 'exact', head: true }),
  ]);
  return {
    orgs: orgs.count ?? 0,
    sites: sites.count ?? 0,
    bills: bills.count ?? 0,
    contacts: contacts.count ?? 0,
    attachments: attachments.count ?? 0,
  };
}

export function AdminPage() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const s = await loadStats();
      setStats(s);
    } catch {
      // Stats are informational — ignore errors
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const STAT_ITEMS = [
    { label: 'Entities', value: stats?.orgs,        icon: Building2,  color: 'text-blue-600 bg-blue-50' },
    { label: 'Sites (NMIs)',   value: stats?.sites,       icon: MapPin,     color: 'text-indigo-600 bg-indigo-50' },
    { label: 'Bills',          value: stats?.bills,       icon: ReceiptText, color: 'text-purple-600 bg-purple-50' },
    { label: 'Contacts',       value: stats?.contacts,    icon: Users,      color: 'text-orange-600 bg-orange-50' },
    { label: 'Attachments',    value: stats?.attachments, icon: Paperclip,  color: 'text-green-600 bg-green-50' },
  ];

  const COMING_SOON = [
    { title: 'Delete Bill Run',             desc: 'Search, review and permanently delete bill runs across all sites and years' },
    { title: 'Retailer Rate Management',    desc: 'Manage default retail rates and tariff schedules across all sites' },
    { title: 'Distribution Config Templates', desc: 'Create reusable distribution config templates for common network types' },
    { title: 'Variance Threshold Settings', desc: 'Configure overcharge/undercharge thresholds per entity' },
    { title: 'Audit Log',                   desc: 'View all bill calculation events and manual data entries' },
    { title: 'Bulk Import',                 desc: 'Import site and contract data from CSV or spreadsheet' },
    { title: 'User Permissions',            desc: 'Manage which users can create, edit, or delete bill validation records' },
  ];

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 max-w-5xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-gray-100">
          <Settings className="h-6 w-6 text-gray-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Admin</h1>
          <p className="text-sm text-gray-500">System configuration and overview</p>
        </div>
      </div>

      {/* System stats */}
      <Card className="border-0 shadow-sm mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-700 flex items-center gap-2">
            <Database className="h-4 w-4 text-gray-500" />
            System Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {STAT_ITEMS.map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="text-center p-3 rounded-lg bg-gray-50">
                  <div className={`inline-flex p-2 rounded-lg ${color} mb-1.5`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <p className="text-xl font-bold text-gray-900 tabular-nums">{value ?? '—'}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Link to="/bill-validation/admin/jobs" data-testid="admin-link-jobs" className="mb-6 block">
        <Card className="border-0 shadow-sm transition-colors hover:bg-blue-50/40">
          <CardContent className="flex items-center gap-3 py-4">
            <div className="rounded-lg bg-green-50 p-2 text-green-600"><Activity className="h-4 w-4" /></div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800">Jobs: background processes</h4>
              <p className="text-xs text-gray-500">Is each background process alive? Tick, email intake, parsing, bill runs, GCS and report sends.</p>
            </div>
          </CardContent>
        </Card>
      </Link>

      {/* Coming soon features */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-700 flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-gray-500" />
            Upcoming Admin Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {COMING_SOON.map(({ title, desc }) => (
              <div key={title} className="rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                  <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
